import {
  ApplySchemaAttributes,
  command,
  CommandFunction,
  extension,
  ExtensionPriority,
  ExtensionTag,
  isElementDomNode,
  isString,
  joinStyles,
  MarkExtension,
  MarkExtensionSpec,
  MarkSpecOverride,
  omitExtraAttributes,
  ProsemirrorAttributes,
} from "@remirror/core";
import type { StyleParseRule, TagParseRule } from "@remirror/pm/model";

const FONT_FAMILY_ATTRIBUTE = "data-font-family";

export type FontFamilyAttributes = ProsemirrorAttributes<{
  fontFamily?: string;
}>;

/**
 * Custom Font Family Extension that properly quotes font family names in CSS
 */
@extension({
  defaultOptions: {},
})
export class CmFontFamilyExtension extends MarkExtension {
  get name() {
    return "fontFamily" as const;
  }

  createTags() {
    return [ExtensionTag.FontStyle, ExtensionTag.FormattingMark];
  }

  createMarkSpec(
    extra: ApplySchemaAttributes,
    override: MarkSpecOverride
  ): MarkExtensionSpec {
    return {
      ...override,
      attrs: { ...extra.defaults(), fontFamily: { default: null } },
      parseDOM: [
        {
          tag: `span[${FONT_FAMILY_ATTRIBUTE}]`,
          getAttrs: (dom) => {
            if (!isElementDomNode(dom)) {
              return false;
            }

            const fontFamily = dom.getAttribute(FONT_FAMILY_ATTRIBUTE);

            if (!fontFamily) {
              return false;
            }

            return { ...extra.parse(dom), fontFamily };
          },
        } satisfies TagParseRule,
        {
          // Get the font-family from the css style property. This is useful for pasted content.
          style: "font-family",
          priority: ExtensionPriority.Low,
          getAttrs: (fontFamily) => {
            if (!isString(fontFamily)) {
              return false;
            }

            return {
              fontFamily: fontFamily ? fontFamily.replace(/["']/g, "") : "",
            };
          },
        } satisfies StyleParseRule,
        ...(override.parseDOM ?? []),
      ],
      toDOM: (mark) => {
        const { fontFamily } = omitExtraAttributes(
          mark.attrs,
          extra
        ) as FontFamilyAttributes;
        const extraAttrs = extra.dom(mark);
        let style = extraAttrs.style;

        if (fontFamily) {
          // Properly quote font family names, especially those with spaces
          const quotedFontFamily = this.quoteFontFamily(fontFamily);
          style = joinStyles({ fontFamily: quotedFontFamily }, style);
        }

        return [
          "span",
          { ...extraAttrs, style, [FONT_FAMILY_ATTRIBUTE]: fontFamily },
          0,
        ];
      },
    };
  }

  /**
   * Properly quotes font family names that contain spaces or special characters
   */
  private quoteFontFamily(fontFamily: string): string {
    // If already quoted, return as-is
    if (
      (fontFamily.startsWith('"') && fontFamily.endsWith('"')) ||
      (fontFamily.startsWith("'") && fontFamily.endsWith("'"))
    ) {
      return fontFamily;
    }

    // If contains spaces or special characters, add quotes
    if (fontFamily.includes(" ") || /[^a-zA-Z0-9-]/.test(fontFamily)) {
      return `'${fontFamily}'`;
    }

    return fontFamily;
  }

  /**
   * Set the font family for the selected text.
   */
  @command()
  setFontFamily(fontFamily: string): CommandFunction {
    return this.store.commands.applyMark.original(this.type, { fontFamily });
  }

  /**
   * Set the font family for the selected text.
   */
  @command()
  toggleFontFamily(fontFamily: string): CommandFunction {
    return this.store.commands.toggleMark.original({
      type: this.type,
      attrs: { fontFamily },
    });
  }
}
